import type { SocialProvider } from './social.js';
import { SOCIAL_LABEL } from './social.js';

/**
 * The providers' own marks (ADR 189). Their colours are the providers' brand
 * rules, not ours, so this file is exempt from the raw-colour lint (like
 * brand.tsx); nothing else may copy these values.
 */
export function SocialIcon({ provider }: { provider: SocialProvider }) {
  if (provider === 'Google')
    return (
      <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        <path
          fill="#FFC107"
          d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
        />
        <path
          fill="#FF3D00"
          d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
        />
      </svg>
    );
  if (provider === 'SignInWithApple')
    return (
      <svg width="18" height="20" viewBox="0 0 17 20" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M14.1 10.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.8 1.2 1.8 2.6 3.1 2.5 1.3-.1 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4 1-1.4 1.3-2.7 1.4-2.8-.1 0-2.6-1-2.6-4.2zM11.6 3c.7-.8 1.2-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z"
        />
      </svg>
    );
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/** "Continue with Google" and friends: one pill button per configured provider. */
export function SocialButtons({
  providers,
  onChoose,
  disabled,
  verb = 'Continue with',
}: {
  providers: readonly SocialProvider[];
  onChoose: (provider: SocialProvider) => void;
  disabled?: boolean;
  verb?: string;
}) {
  if (providers.length === 0) return null;
  return (
    <div className="signin-social">
      {providers.map((p) => (
        <button
          key={p}
          type="button"
          className="signin-social-btn"
          disabled={disabled}
          onClick={() => onChoose(p)}
        >
          <span className="signin-social-icon">
            <SocialIcon provider={p} />
          </span>
          {`${verb} ${SOCIAL_LABEL[p]}`}
        </button>
      ))}
    </div>
  );
}

/** The thin "or" rule between the form and the provider buttons. */
export function OrRule() {
  return (
    <div className="signin-or" role="separator">
      <span>or</span>
    </div>
  );
}
